import React, { useState, useEffect, useRef } from 'react';
import { listVendors, addVendor, deleteVendor, searchCompanies, updateVendor } from '../../services/api';
import { buildVendorWebsiteUrl } from '../../lib/vendorUtils';
import ConfirmDialog from '../ConfirmDialog';
import '../../styles/dashboard-pages.css';

const NETWORK_CENTER = { x: 320, y: 230 };
const NETWORK_RING_CAPACITY = 8;

const getNetworkNodePosition = (index, total) => {
  const innerRingCount = Math.min(total, NETWORK_RING_CAPACITY);
  const isOuterRing = index >= innerRingCount;
  const ringIndex = isOuterRing ? index - innerRingCount : index;
  const ringCount = isOuterRing ? total - innerRingCount : innerRingCount;
  const radius = isOuterRing ? 180 : total > NETWORK_RING_CAPACITY ? 108 : 150;
  const angleOffset = -Math.PI / 2;
  const angle = angleOffset + (ringIndex * 2 * Math.PI) / Math.max(ringCount, 1);

  return {
    x: NETWORK_CENTER.x + radius * Math.cos(angle),
    y: NETWORK_CENTER.y + radius * Math.sin(angle),
  };
};

const getVendorInitials = (name = '') => {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

  return initials || 'V';
};

function MyDependencies() {
  const [vendors, setVendors] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);
  const selectingRef = useRef(false);
  const [vendorEmailLocal, setVendorEmailLocal] = useState('');
  const [vendorDomain, setVendorDomain] = useState('');
  const [newVendor, setNewVendor] = useState({
    vendor_id: null,
    name: '',
    importance_weight: 2,
    contact: '',
    website_url: ''
  });
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [editVendorData, setEditVendorData] = useState({ importance_weight: 2, contact: '' });
  const [vendorToRemove, setVendorToRemove] = useState(null);
  const [removingVendor, setRemovingVendor] = useState(false);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const data = await listVendors();
        setVendors(data);
      } catch (err) {
        console.error('Failed to fetch vendors:', err);
        setVendors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
  }, []);

  // Handle vendor search with debouncing
  useEffect(() => {
    if (selectingRef.current) {
      selectingRef.current = false;
      return;
    }

    if (newVendor.name.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const results = await searchCompanies(newVendor.name);
        setSuggestions(results || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Search failed:', error);
        setSuggestions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [newVendor.name]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle suggestion click
  const handleSuggestionClick = (company) => {
    selectingRef.current = true;
    const domain = company.domain || '';
    setNewVendor({
      ...newVendor,
      name: company.normalized_name,
      vendor_id: company.id,
      contact: '',
      website_url: buildVendorWebsiteUrl(domain),
    });
    setVendorDomain(domain);
    setVendorEmailLocal('');
    setSuggestions([]);
    setShowSuggestions(false);
    // Blur the input to prevent onFocus from reopening suggestions
    searchRef.current?.querySelector('input')?.blur();
  };

  const handleAddVendor = async (e) => {
    e.preventDefault();
    
    if (!newVendor.name.trim()) {
      console.error('Vendor name is required');
      return;
    }
    
    if (!newVendor.contact.trim() && !newVendor.website_url.trim()) {
      console.error('Contact email or website URL is required');
      return;
    }

    try {
      await addVendor(
        newVendor.vendor_id,
        newVendor.importance_weight,
        newVendor.contact,
        newVendor.name,
        newVendor.website_url
      );

      // Refetch the vendor list to get complete data including scores
      const updatedVendors = await listVendors();
      setVendors(updatedVendors);
      
      setNewVendor({ vendor_id: null, name: '', importance_weight: 2, contact: '', website_url: '' });
      setVendorDomain('');
      setVendorEmailLocal('');
      setShowAddForm(false);
    } catch (err) {
      console.error('Failed to add vendor:', err);
    }
  };

  const handleRemoveVendor = async () => {
    if (!vendorToRemove || removingVendor) return;
    try {
      setRemovingVendor(true);
      await deleteVendor(vendorToRemove.id);
      setVendors((currentVendors) => currentVendors.filter((vendor) => vendor.id !== vendorToRemove.id));
      setVendorToRemove(null);
    } catch (err) {
      console.error('Failed to delete vendor:', err);
    } finally {
      setRemovingVendor(false);
    }
  };

  const getImportanceLabel = (level) => {
    switch(level) {
      case 1: return 'Tier 1';
      case 2: return 'Tier 2';
      case 3: return 'Tier 3';
      default: return '';
    }
  };

  return (
    <div className="dependencies-page">
      <div className="dependencies-hero">
        <div className="page-header dependencies-page-header">
          <span className="dependencies-eyebrow">Third-party risk</span>
          <h2>My Dependencies</h2>
          <p>Keep your vendor network organized and focus attention on the relationships that matter most.</p>
        </div>

        <div className="dependencies-summary" aria-label="Dependency overview">
          <div className="dependency-summary-card">
            <span className="material-symbols-outlined">hub</span>
            <div>
              <strong>{vendors.length}</strong>
              <small>Total vendors</small>
            </div>
          </div>
        </div>
      </div>

      <div className="dependencies-grid">
        {/* Vendors List */}
        <div className="vendors-list-section">
          <div className="section-header">
            <div>
              <span className="section-kicker">Vendor inventory</span>
              <h3>Declared Vendors</h3>
            </div>
            <button 
              className={`btn btn-small ${showAddForm ? '' : 'btn-primary'}`}
              onClick={() => setShowAddForm(!showAddForm)}
              aria-expanded={showAddForm}
            >
              <span className="material-symbols-outlined">{showAddForm ? 'close' : 'add'}</span>
              {showAddForm ? 'Close' : 'Add Vendor'}
            </button>
          </div>

          {loading ? (
            <div className="dependencies-state">
              <span className="material-symbols-outlined dependencies-state-icon">progress_activity</span>
              <p>Loading your vendor network...</p>
            </div>
          ) : (
            <>
              {showAddForm && (
                <form className="add-vendor-form" onSubmit={handleAddVendor}>
                  <div className="vendor-search-wrapper" ref={searchRef} style={{ width: '100%' }}>
                    <input 
                      type="text" 
                      placeholder="Vendor Name"
                      value={newVendor.name}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewVendor({
                          ...newVendor,
                          name: value,
                          vendor_id: null,
                          contact: '',
                          website_url: '',
                        });
                        setVendorDomain('');
                        setVendorEmailLocal('');
                      }}
                      onFocus={() => newVendor.name.trim().length >= 2 && setShowSuggestions(true)}
                      required
                      style={{ width: '100%' }}
                    />
                    {searchLoading && <span className="search-loading">...</span>}

                    {showSuggestions && suggestions.length > 0 && (
                      <div className="vendor-suggestions">
                        {suggestions.map((company) => (
                          <div
                            key={company.id}
                            className="suggestion-item"
                            onClick={() => handleSuggestionClick(company)}
                          >
                            <div className="suggestion-content">
                              <div className="suggestion-name">{company.normalized_name}</div>
                              <div className="suggestion-domain">{company.domain || 'N/A'}</div>
                            </div>
                          </div>
                        ))}
                        {!searchLoading && (
                          <div
                            className="suggestion-item"
                            onClick={() => {
                              selectingRef.current = true;
                                setNewVendor({
                                  ...newVendor,
                                  vendor_id: null,
                                  contact: '',
                                  website_url: '',
                                });
                              setVendorDomain('');
                              setVendorEmailLocal('');
                              setShowSuggestions(false);
                              searchRef.current?.querySelector('input')?.blur();
                            }}
                          >
                            <div className="suggestion-content">
                              <div className="suggestion-name">{newVendor.name}</div>
                              <div className="suggestion-domain">Custom vendor</div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {showSuggestions && newVendor.name.trim().length >= 2 && suggestions.length === 0 && !searchLoading && (
                      <div className="vendor-suggestions">
                        <div
                          className="suggestion-item"
                          onClick={() => {
                            selectingRef.current = true;
                            setNewVendor({
                              ...newVendor,
                              vendor_id: null,
                              contact: '',
                              website_url: '',
                            });
                            setVendorDomain('');
                            setVendorEmailLocal('');
                            setShowSuggestions(false);
                            searchRef.current?.querySelector('input')?.blur();
                          }}
                        >
                          <div className="suggestion-content">
                            <div className="suggestion-name">{newVendor.name}</div>
                            <div className="suggestion-domain">Custom vendor</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <select
                    value={newVendor.importance_weight}
                    onChange={(e) => setNewVendor({...newVendor, importance_weight: parseInt(e.target.value)})}
                  >
                    <option value={1}>Tier 1 (Lowest)</option>
                    <option value={2}>Tier 2 (Important)</option>
                    <option value={3}>Tier 3 (Most Critical)</option>
                  </select>

                  <div className="tier-guide">
                    <p className="tier-guide-title">Tier Guide</p>
                    <div className="tier-guide-item"><strong>Tier 1:</strong> Limited or non-critical data handling.</div>
                    <div className="tier-guide-item"><strong>Tier 2:</strong> Important operational dependency with meaningful data access.</div>
                    <div className="tier-guide-item"><strong>Tier 3:</strong> Mission-critical dependency or highly sensitive data access.</div>
                  </div>

                  {vendorDomain ? (
                    <div className="vendor-email-input">
                      <input
                        type="text"
                        placeholder="Security Contact Email"
                        value={vendorEmailLocal}
                        onChange={(e) => {
                          const local = e.target.value.split('@')[0];
                          setVendorEmailLocal(local);
                          const fullEmail = local ? `${local}@${vendorDomain}` : '';
                          setNewVendor({ ...newVendor, contact: fullEmail });
                        }}
                      />
                      <span className="email-domain">@{vendorDomain}</span>
                    </div>
                  ) : (
                    <input 
                      type="email" 
                      placeholder="Security Contact Email"
                      value={newVendor.contact}
                      onChange={(e) => setNewVendor({...newVendor, contact: e.target.value})}
                    />
                  )}

                  <input
                    type="url"
                    value={newVendor.website_url}
                    onChange={(e) => setNewVendor({ ...newVendor, website_url: e.target.value })}
                    readOnly={Boolean(vendorDomain)}
                    placeholder={vendorDomain ? 'Website URL (prefilled from company domain)' : 'Website URL (optional if email provided)'}
                  />

                  <div className="form-actions">
                    <button type="submit" className="btn btn-small btn-primary">Add</button>
                    <button 
                      type="button" 
                      className="btn btn-small"
                      onClick={() => {
                        setShowAddForm(false);
                        setVendorDomain('');
                        setVendorEmailLocal('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              <div className="vendor-cards">
                {vendors.length === 0 && !showAddForm && (
                  <div className="dependencies-state dependencies-empty-state">
                    <span className="material-symbols-outlined dependencies-state-icon">account_tree</span>
                    <h4>Build your vendor network</h4>
                    <p>Add the services and partners your organization relies on to start tracking dependency risk.</p>
                    <button className="btn btn-small btn-primary" onClick={() => setShowAddForm(true)}>
                      <span className="material-symbols-outlined">add</span>
                      Add your first vendor
                    </button>
                  </div>
                )}
                {vendors.map((vendor) => (
                  <div key={vendor.id} className="dependency-vendor-card">
                    <div className="dependency-vendor-header">
                      <div className="dependency-vendor-identity">
                        <span className="dependency-vendor-avatar">
                          {getVendorInitials(vendor.vendor_name)}
                        </span>
                        <div>
                          <h4>{vendor.vendor_name}</h4>
                          <span className={`badge importance-${vendor.importance_weight}`}>
                            {getImportanceLabel(vendor.importance_weight)}
                          </span>
                        </div>
                      </div>
                      <div className="vendor-actions">
                        <button
                          className="icon-btn"
                          title={`Edit ${vendor.vendor_name}`}
                          aria-label={`Edit ${vendor.vendor_name}`}
                          onClick={() => {
                            setEditingVendorId(vendor.id);
                            setEditVendorData({ importance_weight: vendor.importance_weight || 2, contact: vendor.cybersecurity_contact_email || '' });
                          }}
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </button>
                        <button 
                          className="icon-btn danger"
                          title={`Remove ${vendor.vendor_name}`}
                          aria-label={`Remove ${vendor.vendor_name}`}
                          onClick={() => setVendorToRemove(vendor)}
                        >
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </div>
                    </div>
                    <div className="vendor-info">
                      {editingVendorId === vendor.id ? (
                        <div className="edit-vendor-form">
                          <label>
                            Importance:
                            <select
                              value={editVendorData.importance_weight}
                              onChange={(e) => setEditVendorData({ ...editVendorData, importance_weight: parseInt(e.target.value) })}
                            >
                              <option value={1}>Tier 1 (Lowest)</option>
                              <option value={2}>Tier 2 (Important)</option>
                              <option value={3}>Tier 3 (Most Critical)</option>
                            </select>
                          </label>

                          <label>
                            Contact:
                            <input
                              type="email"
                              value={editVendorData.contact}
                              onChange={(e) => setEditVendorData({ ...editVendorData, contact: e.target.value })}
                            />
                          </label>

                          <div className="form-actions">
                            <button
                              className="btn btn-small btn-primary"
                              onClick={async () => {
                                try {
                                  await updateVendor(vendor.id, editVendorData.importance_weight, editVendorData.contact);
                                  const updated = await listVendors();
                                  setVendors(updated);
                                  setEditingVendorId(null);
                                } catch (err) {
                                  console.error('Failed to update vendor:', err);
                                }
                              }}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-small"
                              onClick={() => setEditingVendorId(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="dependency-vendor-score">
                            <div>
                              <span>Security score</span>
                              <strong>{vendor.final_score ?? 'N/A'}</strong>
                            </div>
                            <div className="dependency-score-track" aria-hidden="true">
                              <span style={{ width: `${Math.max(0, Math.min(Number(vendor.final_score) || 0, 100))}%` }} />
                            </div>
                          </div>
                          <div className="dependency-vendor-contact">
                            <span className="material-symbols-outlined">mail</span>
                            <span>{vendor.cybersecurity_contact_email || 'No security contact added'}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Network Visualization */}
        <div className="network-map-section">
          <div className="network-map-header">
            <div>
              <span className="section-kicker">Relationship view</span>
              <h3>Network Map</h3>
            </div>
            <span className="network-count">{vendors.length} connected</span>
          </div>
          <div className="network-visualization">
            <div className="network-canvas">
              <svg viewBox="0 0 640 460" className="network-svg" role="img" aria-label={`Network map with ${vendors.length} vendors`}>
                <defs>
                  <radialGradient id="networkCenterGlow">
                    <stop offset="0%" stopColor="#a0ffc3" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#a0ffc3" stopOpacity="0" />
                  </radialGradient>
                </defs>
                <circle cx={NETWORK_CENTER.x} cy={NETWORK_CENTER.y} r="72" fill="url(#networkCenterGlow)" />

                {vendors.map((vendor, index) => {
                  const { x, y } = getNetworkNodePosition(index, vendors.length);
                  const isCritical = vendor.importance_weight === 3;
                  const isLowImportance = vendor.importance_weight === 1;
                  const nodeClass = isCritical ? 'is-critical' : isLowImportance ? 'is-low' : 'is-medium';

                  return (
                    <g key={vendor.id} className={`network-vendor-node ${nodeClass}`}>
                      <line x1={NETWORK_CENTER.x} y1={NETWORK_CENTER.y} x2={x} y2={y} className="network-link" />
                      <circle cx={x} cy={y} r="24" className="network-node-halo" />
                      <circle cx={x} cy={y} r="18" className="network-node-circle" />
                      <text x={x} y={y + 4} textAnchor="middle" className="network-node-initials">
                        {getVendorInitials(vendor.vendor_name)}
                      </text>
                      <text x={x} y={y + 38} textAnchor="middle" className="network-node-label">
                        {vendor.vendor_name.length > 16 ? `${vendor.vendor_name.slice(0, 15)}...` : vendor.vendor_name}
                      </text>
                      <title>{`${vendor.vendor_name} - ${getImportanceLabel(vendor.importance_weight)}`}</title>
                    </g>
                  );
                })}

                <g className="network-center-node">
                  <circle cx={NETWORK_CENTER.x} cy={NETWORK_CENTER.y} r="31" className="network-center-halo" />
                  <circle cx={NETWORK_CENTER.x} cy={NETWORK_CENTER.y} r="24" className="network-center-circle" />
                  <text x={NETWORK_CENTER.x} y={NETWORK_CENTER.y + 4} textAnchor="middle" className="network-center-icon">YOU</text>
                </g>
              </svg>
              {vendors.length === 0 && (
                <div className="network-empty-message">
                  <span className="material-symbols-outlined">add_link</span>
                  <p>Your relationships will appear here as vendors are added.</p>
                </div>
              )}
            </div>
            <div className="network-legend">
              <div className="legend-item">
                <span className="color critical"></span>
                <span>Tier 3</span>
              </div>
              <div className="legend-item">
                <span className="color medium"></span>
                <span>Tier 2</span>
              </div>
              <div className="legend-item">
                <span className="color low"></span>
                <span>Tier 1</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(vendorToRemove)}
        title="Remove vendor?"
        description={`${vendorToRemove?.vendor_name || 'This vendor'} will be removed from your dependency network. This action cannot be undone.`}
        confirmLabel="Remove Vendor"
        loading={removingVendor}
        onConfirm={handleRemoveVendor}
        onCancel={() => setVendorToRemove(null)}
      />
    </div>
  );
}

export default MyDependencies;
